import React from 'react';
export interface SeriesPoint {
  /** Short axis label, e.g. "12 Aug". */
  label: string;
  value: number;
}
interface BarSeriesProps {
  points: SeriesPoint[];
  /** What one bar counts. Read out with the chart. */
  unit: string;
  caption: string;
}

/**
 * A hand-drawn bar series rather than a charting dependency: this is one chart
 * of counted events, and a chart library would add weight without adding
 * meaning here.
 *
 * Accessible by construction — the same numbers exist as a real table for a
 * screen reader, so the chart is a second reading of the data rather than the
 * only one. Colour comes from tokens, so both themes are correct.
 */
export function BarSeries({
  points,
  unit,
  caption
}: BarSeriesProps) {
  const max = Math.max(...points.map(point => point.value), 1);
  return <figure className="min-w-0" data-id="element-391" data-mp="2xnf1ndj06u0u">
      <div className="flex h-40 items-end gap-1.5" role="presentation"
    // The table below is the accessible reading; this is decorative.
    aria-hidden="true" data-id="element-392" data-mp="2xoq336oz7pos">
        {points.map(point => {
        const height = Math.max(point.value / max * 100, point.value > 0 ? 4 : 1.5);
        return <div key={point.label} className="flex min-w-0 flex-1 flex-col items-center gap-1.5" data-id="element-393" data-mp="29h9opn7pxa6y">
              <div className="flex w-full flex-1 items-end" data-id="element-394" data-mp="2dibeldfqw9w9">
                <div className={`w-full rounded-t ${point.value > 0 ? 'bg-brand-500' : 'bg-line'}`} style={{
              height: `${height}%`
            }} title={`${point.label}: ${point.value} ${unit}`} data-id="element-395" data-mp="2dgrypny040v0" />
              </div>
              <span className="w-full truncate text-center text-[0.6875rem] leading-4 text-ink-subtle" data-id="element-396" data-mp="1pu20kxorwqfo">
                {point.label}
              </span>
            </div>;
      })}
      </div>

      <figcaption className="mt-2 text-caption leading-5 text-ink-subtle" data-id="element-397" data-mp="1gtnall0v6n5u">{caption}</figcaption>

      <table className="sr-only" data-id="element-398" data-mp="1lq3xyd08oxhv">
        <caption data-id="element-399" data-mp="1lthydfpcewmo">{caption}</caption>
        <thead data-id="element-400" data-mp="3nh4h6qp8i7qf">
          <tr data-id="element-401" data-mp="3nhmk266ejk7b">
            <th scope="col" data-id="element-402" data-mp="3nhcqk8m5euwv">Day</th>
            <th scope="col" data-id="element-403" data-mp="3niflcrmldhi2">{unit}</th>
          </tr>
        </thead>
        <tbody data-id="element-404" data-mp="3iobcu3xoh1ev">
          {points.map(point => <tr key={point.label} data-id="element-405" data-mp="3dq9slfoimw1t">
              <th scope="row" data-id="element-406" data-mp="3drkx5hpsc7tz">{point.label}</th>
              <td data-id="element-407" data-mp="39p6fh0xure4a">{point.value}</td>
            </tr>)}
        </tbody>
      </table>
    </figure>;
}